import { CancelToken, isCancel } from 'axios';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Card } from '@hivebrite/design-system/Card';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { Slider } from '@hivebrite/design-system/Slider';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { getScreenReaderStatus } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { useEventsIndexContext } from 'hb-react/shared/apps/Events/contexts/EventsIndexContext';
import {
  Col,
  Row,
  WrappingRow,
} from 'hb-react/shared/components/DesignSystem/Grid';
import { CANCEL_FETCH_MESSAGE } from 'hb-react/shared/constants/cancelFetch';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { buildItems } from '../utils';

const SLIDER_CARD_HEIGHT = {
  s: 170,
  m: 149,
  l: 157,
  xl: 168,
};

const SliderWrapper = ({
  loading,
  processedItems,
  total,
  perPageBreakpoints,
  handleLoadMore,
  ItemComponent,
  ItemLoadingComponent,
}) => (
  <Box flexDirection="column" mt={5}>
    <Slider
      loading={loading}
      items={processedItems}
      total={total}
      perPageBreakpoints={perPageBreakpoints}
      onLoadMore={handleLoadMore}
      ItemComponent={ItemComponent}
      LoadingItemComponent={ItemLoadingComponent}
      pictureHeight={SLIDER_CARD_HEIGHT}
      pictureSpace={14}
    />
  </Box>
);

const EventsCards = ({
  colWidthBreakpoints,
  fetchPath,
  index,
  ItemComponent,
  ItemLoadingComponent,
  maxPerPage,
  name,
  noResultsMessage,
  onClearFilters,
  params,
  perPageBreakpoints,
  prefetchPages,
  resultsFoundMessage,
  showIfEmpty = false,
  singleLine,
}) => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const breakpoint = useBreakpoint();
  const {
    eventBeingDeleted,
    hiddenUsersIds,
    isMapDisplayed,
    onEventCardClick,
    selectedEvent,
    subscribeToEventDeletedHandlers,
    unsubscribeFromEventDeletedHandlers,
  } = useEventsIndexContext();

  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(undefined);
  const [items, setItems] = useState([]);

  // Because we don't display the same number of items per "page" on every resolution,
  // we need to decouple the page we use to load the items and the page we use to display
  // the items in order to be able to display the correct amount of items even if
  // the user resizes between breakpoints.
  const [fetchedPage, setFetchedPage] = useState(0);

  const cancelTokenFetchItems = useRef(null);

  const fetchItems = useCallback(
    (pageToFetch, perPageOverride) => {
      if (cancelTokenFetchItems.current) {
        cancelTokenFetchItems.current.cancel();
      } else {
        setLoading(true);
      }

      cancelTokenFetchItems.current = CancelToken.source(CANCEL_FETCH_MESSAGE);

      axios
        .get(
          fetchPath({
            format: 'json',
            ...params,
            page: pageToFetch,
            per_page: perPageOverride || maxPerPage,
          }),
          {
            cancelToken: cancelTokenFetchItems.current.token,
          },
        )
        .then((response) => {
          cancelTokenFetchItems.current = null;
          setItems((prev) => {
            const newItems = [...prev, ...response.data.events];

            setFetchedPage(Math.ceil(newItems.length / maxPerPage));

            return newItems;
          });
          setTotal(response.data.total_items);
          setLoading(false);
        })
        .catch((error) => {
          if (isCancel(error)) {
            return;
          }

          // Since the App is doing multiple fetch (1 by EventsCards), avoid to spam end-user with errors.
          if (index === 0) {
            displayXhrError(error);
          }
        });
    },
    [maxPerPage, params],
  );

  const handleLoadMore = useCallback(() => {
    // Fetch new items if needed
    if (items.length < total) {
      fetchItems(fetchedPage + 1);
    }
  }, [items, total, fetchedPage, fetchItems]);

  const handleEventDeleted = useCallback((itemToDelete) => {
    setItems((prev) =>
      prev.filter(
        (e) => e.id !== itemToDelete.id || e.type !== itemToDelete.type,
      ),
    );
  }, []);

  useEffect(() => {
    subscribeToEventDeletedHandlers(handleEventDeleted);

    return () => unsubscribeFromEventDeletedHandlers(handleEventDeleted);
  }, [subscribeToEventDeletedHandlers]);

  // Start fresh initially and when the query params changed
  useEffect(() => {
    setTotal(0);
    setItems([]);
    setFetchedPage(0);
    fetchItems(1, prefetchPages * maxPerPage);
  }, [params.query]);

  const hasNoResults = !loading && items.length === 0;

  const screenReaderStatus = useMemo(
    () =>
      getScreenReaderStatus({
        isLoading: loading,
        itemsSize: total,
        hasNoResult: hasNoResults,
        resultsFoundMessage,
        noResultsMessage,
      }),
    [resultsFoundMessage, noResultsMessage, hasNoResults, loading, total],
  );

  if (!items.length && !loading && !showIfEmpty) {
    return null;
  }

  const processedItems = buildItems({
    eventBeingDeleted,
    events: items,
    hiddenUsersIds,
    isMapDisplayed,
    onEventCardClick,
    selectedEvent,
  });

  return (
    <>
      <ScreenReadersContent aria-live="polite" role="status">
        {screenReaderStatus}
      </ScreenReadersContent>
      <Row mb="-4px" mt={2}>
        <Col width={1}>
          <ThemeProvider
            theme={outboxedFontTheme.themeName}
            fontBrightness={outboxedFontTheme.fontBrightness}
          >
            <Box as="h2" font="h2-semibold">
              {name} {loading && total === 0 ? '' : `(${total})`}
            </Box>
          </ThemeProvider>
        </Col>
      </Row>
      {showIfEmpty && hasNoResults && (
        <Row>
          <Col width={colWidthBreakpoints[breakpoint]}>
            <Box width="100%">
              <Card width="100%">
                <Box font="h4-regular" mb={3}>
                  {__('No matches found based on your search criteria')}
                </Box>
                <Button onClick={onClearFilters}>
                  {__('Clear all filters')}
                </Button>
              </Card>
            </Box>
          </Col>
        </Row>
      )}
      {singleLine ? (
        <SliderWrapper
          loading={loading}
          processedItems={processedItems}
          total={total}
          perPageBreakpoints={perPageBreakpoints}
          handleLoadMore={handleLoadMore}
          ItemComponent={ItemComponent}
          ItemLoadingComponent={ItemLoadingComponent}
          colWidthBreakpoints={colWidthBreakpoints}
        />
      ) : (
        <WrappingRow
          loading={loading}
          items={processedItems}
          total={total}
          perPageBreakpoints={perPageBreakpoints}
          colWidthBreakpoints={colWidthBreakpoints}
          onLoadMore={handleLoadMore}
          ItemComponent={ItemComponent}
          LoadingItemComponent={ItemLoadingComponent}
        />
      )}
    </>
  );
};

export default EventsCards;
